// src/pages/UsersPage.js
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import Card from "../ui/Card";
import Stat from "../ui/Stat";
import Table from "../ui/Table";
import {
  deleteOrgUser,
  fetchOrgUsers,
  saveOrgUser,
  updateOrgUser,
} from "../utils/orgUsers";
import { updateAuthoritativeOrgUserRole } from "../utils/orgRoleUpdate";
import { logOrgActivity } from "../utils/orgAudit";
import { auth } from "../firebase";

const PLACEHOLDER_PROJECT_RE = /your_project/i;

function resolveFunctionsBaseUrl(projectId) {
  const envBase = String(process.env.REACT_APP_FUNCTIONS_BASE_URL || "").trim();
  if (envBase && !PLACEHOLDER_PROJECT_RE.test(envBase)) {
    if (projectId && envBase.includes(`africa-south1-${projectId}.cloudfunctions.net`)) {
      return envBase
        .replace(`africa-south1-${projectId}.cloudfunctions.net`, `europe-west1-${projectId}.cloudfunctions.net`)
        .replace(/\/+$/, "");
    }
    return envBase.replace(/\/+$/, "");
  }
  return projectId ? `https://europe-west1-${projectId}.cloudfunctions.net` : "";
}

function formatDateTime(value) {
  if (!value) return "—";
  const ts = typeof value === "number" ? value : Date.parse(String(value));
  if (!Number.isFinite(ts)) return "—";
  return new Date(ts).toLocaleString();
}

function getPilotStatus(u) {
  if (u?.lastLogin || u?.lastSignInTime || u?.metadata?.lastSignInTime) {
    return "Active";
  }
  if (u?.hasOnboardingEmailSent) return "Invited";
  return "Pending";
}

function roleLabel(value) {
  const role = String(value || "member").trim().toLowerCase();
  if (role === "superadmin") return "Super Admin";
  if (role === "admin") return "Admin";
  if (role === "coach") return "Coach";
  if (role === "investor") return "Investor";
  return "Member";
}

export default function UsersPage() {
  const { user, activeOrgId } = useAuth();
  const isReadOnly = user?.role === "investor";
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [showDisabled, setShowDisabled] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [pendingStatus, setPendingStatus] = useState("");
  const [addUserStatus, setAddUserStatus] = useState("");
  const [reminderStatus, setReminderStatus] = useState("");
  const [roleUpdateStatus, setRoleUpdateStatus] = useState("");
  const [sendingReminderById, setSendingReminderById] = useState({});

  // form state
  const [username, setUsername]   = useState("");
  const [firstName, setFirstName] = useState("");
  const [surname, setSurname]     = useState("");
  const [email, setEmail]         = useState("");
  const isFormValid = useMemo(
    () => Boolean(firstName.trim() && surname.trim() && email.trim()),
    [email, firstName, surname]
  );

  const handleAddUser = async () => {
    if (!firstName || !surname || !email) return;

    const normalizedEmail = String(email || "").trim().toLowerCase();
    const newUsername =
      username?.trim() ||
      `${(firstName[0] || "").toLowerCase()}${(surname || "").toLowerCase()}`;

    const orgId = activeOrgId || "org_default";
    const currentUser = auth.currentUser;
    const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || "";
    const base = resolveFunctionsBaseUrl(projectId);

    setAddUserStatus("Creating user...");
    try {
      // Preferred path: provision through existing secure backend flow used by Invite page.
      if (currentUser && base) {
        const token = await currentUser.getIdToken();
        const endpoint = `${base}/createOrgUser`;
        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            email: normalizedEmail,
            role: "member",
            orgId,
            username: newUsername,
            firstName: firstName.trim(),
            surname: surname.trim(),
            loginUrl: `${window.location.origin}/login`,
          }),
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || "Unable to create user.");
        }
        const refreshed = await fetchOrgUsers(user, orgId);
        setUsers(refreshed);
        setAddUserStatus(String(payload?.message || "User created."));
      } else {
        // Safe fallback keeps dashboard metadata explicit so records are not misclassified as legacy.
        await saveOrgUser(user, orgId, {
          id: `${newUsername}_${Date.now()}`,
          username: newUsername,
          firstName: firstName.trim(),
          surname: surname.trim(),
          email: normalizedEmail,
          role: "member",
          status: "active",
          createdAt: Date.now(),
          onboardingEmailSent: false,
          lastSignInTime: "",
        });
        const refreshed = await fetchOrgUsers(user, orgId);
        setUsers(refreshed);
        setAddUserStatus("User saved in org directory. Auth provisioning unavailable in this environment.");
      }
      setUsername("");
      setFirstName("");
      setSurname("");
      setEmail("");
    } catch (err) {
      setAddUserStatus(String(err?.message || "User creation failed."));
    }
    setTimeout(() => setAddUserStatus(""), 5000);
  };

  const handleRoleChange = async (id, role) => {
    const target = users.find((u) => u.id === id);
    const previousRole = target?.role || "member";
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, role } : u)));
    setRoleUpdateStatus("Updating role…");
    try {
      const currentUser = auth.currentUser;
      const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || "";
      const base = resolveFunctionsBaseUrl(projectId);
      const orgId = String(activeOrgId || "").trim();
      const result = await updateAuthoritativeOrgUserRole({
        currentUser,
        functionsBaseUrl: base,
        orgId,
        targetUserId: id,
        role,
      });
      await logOrgActivity(user, orgId, "role_change", {
        targetUserId: id,
        targetEmail: target?.email || "",
        role: result.role,
      });
      setRoleUpdateStatus("Role updated. It will apply at the account’s next secure membership refresh.");
    } catch {
      setUsers((prev) => prev.map((entry) => (
        entry.id === id ? { ...entry, role: previousRole } : entry
      )));
      setRoleUpdateStatus("Role update failed.");
    }
  };

  const handleFieldChange = (id, field, value) => {
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, [field]: value } : u)));
  };

  const handleFieldBlur = (id, field) => {
    const target = users.find((u) => u.id === id);
    if (!target?.id) return;
    updateOrgUser(user, activeOrgId || "org_default", target.id, {
      [field]: String(target[field] || "").trim(),
    });
  };

  const handleDelete = (id) => {
    setUsers((prev) => prev.filter((u) => u.id !== id));
    deleteOrgUser(user, activeOrgId || "org_default", id);
  };

  const toggleDisabled = (id) => {
    const target = users.find((u) => u.id === id);
    if (!target?.id) return;
    const nextDisabled = !target.disabled;
    setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, disabled: nextDisabled } : u)));
    updateOrgUser(user, activeOrgId || "org_default", id, { disabled: nextDisabled });
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllVisible = (visible) => {
    setSelected((prev) => {
      const next = new Set(prev);
      visible.forEach((u) => {
        if (u?.id) next.add(u.id);
      });
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const bulkDelete = () => {
    if (!selected.size) return;
    const ids = new Set(selected);
    setUsers((prev) => prev.filter((u) => !ids.has(u.id)));
    ids.forEach((id) => deleteOrgUser(user, activeOrgId || "org_default", id));
    clearSelection();
  };

  const bulkDisable = (disabled) => {
    if (!selected.size) return;
    const ids = new Set(selected);
    setUsers((prev) => prev.map((u) => (ids.has(u.id) ? { ...u, disabled } : u)));
    ids.forEach((id) => updateOrgUser(user, activeOrgId || "org_default", id, { disabled }));
    clearSelection();
  };

  const sendReminder = async (targetUser) => {
    const email = String(targetUser?.email || "").trim().toLowerCase();
    if (!targetUser?.id || !email) return;
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setReminderStatus("You must be signed in to send reminders.");
      return;
    }
    const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID || "";
    const base = resolveFunctionsBaseUrl(projectId);
    if (!base) {
      setReminderStatus("Missing Functions URL config.");
      return;
    }
    setSendingReminderById((prev) => ({ ...prev, [targetUser.id]: true }));
    setReminderStatus("");
    try {
      const token = await currentUser.getIdToken();
      const endpoint = `${base}/sendOrgUserReminder`;
      const orgId = activeOrgId || "org_default";
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email,
          orgId,
          loginUrl: `${window.location.origin}/login`,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.error || "Unable to send reminder.");
      }
      setReminderStatus(`Reminder sent to ${email}.`);
    } catch (err) {
      setReminderStatus(String(err?.message || "Reminder failed."));
    } finally {
      try {
        const refreshed = await fetchOrgUsers(user, activeOrgId || "org_default");
        setUsers(refreshed);
      } catch {}
      setSendingReminderById((prev) => ({ ...prev, [targetUser.id]: false }));
    }
  };

  useEffect(() => {
    let mounted = true;
    fetchOrgUsers(user, activeOrgId || "org_default")
      .then((list) => {
        if (!mounted) return;
        setUsers(list);
      })
      .catch(() => {});
    return () => {
      mounted = false;
    };
  }, [user, activeOrgId]);

  const filteredUsers = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return users.filter((u) => {
      if (!showDisabled && u.disabled) return false;
      if ((u.status || "").toLowerCase() === "pending") return false;
      if (!needle) return true;
      const hay = `${u.username || ""} ${u.firstName || ""} ${u.surname || ""} ${u.email || ""}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [users, query, showDisabled]);

  const pendingInvites = useMemo(() => {
    return users.filter((u) => (u.status || "").toLowerCase() === "pending");
  }, [users]);

  const deletePendingInvites = async () => {
    if (!pendingInvites.length) return;
    const orgId = activeOrgId || "org_default";
    const ok = window.confirm(
      `Delete ${pendingInvites.length} pending invite(s) in this org?\nActive org: ${orgId}`
    );
    if (!ok) return;
    setPendingStatus("Deleting pending invites...");
    const ids = pendingInvites.map((u) => u.id).filter(Boolean);
    const results = await Promise.all(ids.map((id) => deleteOrgUser(user, orgId, id)));
    const failed = results.filter((r) => !r).length;
    try {
      const list = await fetchOrgUsers(user, orgId);
      setUsers(list);
    } catch {}
    if (failed) {
      setPendingStatus(`Some invites could not be deleted (${failed} failed).`);
    } else {
      setPendingStatus("Pending invites cleared.");
    }
    setTimeout(() => setPendingStatus(""), 4000);
  };

  const stats = useMemo(() => {
    const counts = users.reduce(
      (acc, u) => {
        if (u.disabled) return acc;
        if ((u.status || "").toLowerCase() === "pending") return acc;
        const role = (u.role || "Member").toLowerCase();
        acc.total += 1;
        if (role.includes("admin")) acc.admin += 1;
        else if (role.includes("coach")) acc.coach += 1;
        else acc.member += 1;
        return acc;
      },
      { total: 0, admin: 0, coach: 0, member: 0 }
    );
    return counts;
  }, [users]);

  const rows = filteredUsers.map((u, i) => {
    const status = getPilotStatus(u);
    const canSendReminder = !u.lastSignInTime && Boolean(u.email);
    const createdCell = u.createdAt ? formatDateTime(u.createdAt) : "Not recorded";
    const welcomeCell = u.isLinkedUser
      ? (u.hasLinkNotificationSent
          ? (u.linkNotificationSent ? "Linked email sent" : "Linked email not sent")
          : "Linked user")
      : (u.hasOnboardingEmailSent
          ? (u.onboardingEmailSent ? "Sent" : "Not sent")
          : "Not recorded");
    const lastLoginCell = u.lastSignInTime
      ? formatDateTime(u.lastSignInTime)
      : "Never";
    return [
    <input
      key={`${u.id}-select`}
      type="checkbox"
      checked={selected.has(u.id)}
      onChange={() => toggleSelect(u.id)}
      disabled={isReadOnly}
    />,
    i + 1,
    <div style={{ display: "grid", gap: 4 }}>
      <span>{u.username || "-"}</span>
      <span className="nia-muted" style={{ fontSize: 12 }}>{u.email || "-"}</span>
      <div style={{ display: "grid", gap: 6 }}>
        <input
          value={u.firstName || ""}
          onChange={(e) => handleFieldChange(u.id, "firstName", e.target.value)}
          onBlur={() => handleFieldBlur(u.id, "firstName")}
          className="admin-filter table-input"
          placeholder="First name"
          disabled={isReadOnly}
          style={{ maxWidth: 180 }}
        />
        <input
          value={u.surname || ""}
          onChange={(e) => handleFieldChange(u.id, "surname", e.target.value)}
          onBlur={() => handleFieldBlur(u.id, "surname")}
          className="admin-filter table-input"
          placeholder="Surname"
          disabled={isReadOnly}
          style={{ maxWidth: 180 }}
        />
      </div>
      {u.disabled && <span className="nia-pill">Disabled</span>}
    </div>,
    <select
      key={`${u.id || u.username}-role`}
      value={u.role || "member"}
      onChange={(e) => handleRoleChange(u.id, e.target.value)}
      className="admin-filter table-input"
      style={{ maxWidth: 140 }}
      disabled={isReadOnly}
    >
      <option value="superadmin" disabled>Super Admin (global)</option>
      <option value="admin">Admin</option>
      <option value="coach">Coach</option>
      <option value="investor">Investor (Read-only)</option>
      <option value="member">Member</option>
    </select>,
    createdCell,
    welcomeCell,
    lastLoginCell,
    canSendReminder ? (
      <button
        className="btn btn-ghost"
        onClick={() => sendReminder(u)}
        disabled={isReadOnly || Boolean(sendingReminderById[u.id])}
      >
        {sendingReminderById[u.id] ? "Sending..." : "Send reminder"}
      </button>
    ) : "N/A",
    <span className="nia-pill">{status}</span>,
    <div key={`${u.username}-actions`} className="table-actions">
      <button
        className="btn btn-ghost"
        title={u.disabled ? "Enable user" : "Disable user"}
        onClick={() => toggleDisabled(u.id)}
        disabled={isReadOnly}
      >
        {u.disabled ? "✅" : "⛔"}
      </button>
      <button className="btn btn-ghost" title="Delete" onClick={() => handleDelete(u.id)} disabled={isReadOnly}>🗑️</button>
    </div>,
  ];
  });

  return (
    <div className="nia-page">
      <Card className="nia-hero">
        <div className="nia-header">
          <div>
            <h2>User Management</h2>
            <p>Pilot user status dashboard for onboarding, activation, and reminder follow-up.</p>
          </div>
          <span className="nia-pill">{stats.total} total users</span>
        </div>
      </Card>

      <Card title="Search & Bulk Actions">
        <div className="nia-form">
          <label>Search users</label>
          <input
            className="input"
            placeholder="Name, email, username..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={isReadOnly}
          />
          <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <input
              type="checkbox"
              checked={showDisabled}
              onChange={(e) => setShowDisabled(e.target.checked)}
              disabled={isReadOnly}
            />
            Show disabled users
          </label>
          <div className="bulk-actions">
            <button
              className="btn btn-ghost"
              onClick={() => selectAllVisible(filteredUsers)}
              disabled={isReadOnly || !filteredUsers.length}
            >
              Select all visible
            </button>
            <button
              className="btn btn-ghost"
              onClick={clearSelection}
              disabled={isReadOnly || !selected.size}
            >
              Clear selection
            </button>
            <button
              className="btn btn-ghost"
              onClick={bulkDelete}
              disabled={isReadOnly || !selected.size}
            >
              Bulk delete
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => bulkDisable(true)}
              disabled={isReadOnly || !selected.size}
            >
              Bulk disable
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => bulkDisable(false)}
              disabled={isReadOnly || !selected.size}
            >
              Bulk enable
            </button>
          </div>
        </div>
      </Card>

      <div className="nia-grid nia-grid--4">
        <Stat label="Total Users" value={stats.total} subtle="Active profiles" />
        <Stat label="Admins" value={stats.admin} subtle="Org governance" />
        <Stat label="Coaches" value={stats.coach} subtle="Active coaches" />
        <Stat label="Members" value={stats.member} subtle="Employees supported" />
      </div>

      <div className="nia-grid nia-grid--2">
        <Card title="Add New User">
          <div className="nia-form">
            <label>Username (optional)</label>
            <input value={username} onChange={(e) => setUsername(e.target.value)} disabled={isReadOnly} />
            <label>First Name <span className="nia-required">*</span></label>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} required disabled={isReadOnly} />
            <label>Surname <span className="nia-required">*</span></label>
            <input value={surname} onChange={(e) => setSurname(e.target.value)} required disabled={isReadOnly} />
            <label>Email <span className="nia-required">*</span></label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={isReadOnly} />
            <button className="btn btn-primary" onClick={handleAddUser} disabled={isReadOnly || !isFormValid}>
              + Add User
            </button>
            {!isReadOnly && !isFormValid ? (
              <div className="nia-muted">Please complete all required fields</div>
            ) : null}
            {addUserStatus ? <div className="nia-muted">{addUserStatus}</div> : null}
          </div>
        </Card>

        <Card title="Access Controls">
          <div className="nia-list">
            <div className="nia-list-btn">
              <span>Role governance policy</span>
              <span className="nia-pill">Quarterly</span>
            </div>
            <div className="nia-list-btn">
              <span>Invite approvals</span>
              <span className="nia-pill">Admin only</span>
            </div>
            <div className="nia-list-btn">
              <span>Risk response SLA</span>
              <span className="nia-pill">2 hrs</span>
            </div>
          </div>
        </Card>
      </div>

      <Card title="Active Users">
        {roleUpdateStatus ? <div role="status" className="nia-muted" style={{ marginBottom: 10 }}>{roleUpdateStatus}</div> : null}
        {reminderStatus ? <div className="nia-muted" style={{ marginBottom: 10 }}>{reminderStatus}</div> : null}
        <Table
          headers={["", "#", "User", "Role", "Created", "Welcome Email Sent", "Last Login", "Reminder", "Status", "Actions"]}
          rows={rows.length ? rows : [["", "-", "-", "-", "-", "-", "-", "-", "-", "No users yet."]]}
        />
      </Card>

      <Card title="Pending Invites">
        <div className="flex items-center justify-between" style={{ gap: 12 }}>
          <div className="nia-muted">
            Pending invites appear after CSV import. Users still need to self-register with the same email.
          </div>
          <button
            className="btn btn-ghost"
            onClick={deletePendingInvites}
            disabled={isReadOnly || !pendingInvites.length}
          >
            Delete pending invites in this org
          </button>
        </div>
        {pendingStatus && <div className="nia-muted" style={{ marginTop: 8 }}>{pendingStatus}</div>}
        <div style={{ marginTop: 12 }}>
          <Table
            headers={["Name", "Email", "Role", "Invited"]}
            rows={
              pendingInvites.length
                ? pendingInvites.map((u) => [
                    u.username || `${u.firstName || ""} ${u.surname || ""}`.trim() || "-",
                    u.email || "-",
                    roleLabel(u.role),
                    u.invitedAt ? new Date(u.invitedAt).toLocaleDateString() : "-",
                  ])
                : [["-", "-", "-", "No pending invites."]]
            }
          />
        </div>
      </Card>
    </div>
  );
}
